import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, adminHeaders } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, Search, Loader2, Building2, Save } from "lucide-react";

const field = "w-full bg-[#0B0D12] border border-[#2D3748] rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#8B5CF6]";
const TONES = [
  { v: "professionale", l: "Professionale" },
  { v: "informale", l: "Informale" },
  { v: "tecnico", l: "Tecnico" },
];
const empty = { name: "", macro_sector: "", sub_sector: "", tone_of_voice: "professionale", logo_url: "", default_keywords: [], data_source_label: "" };

export default function ClientProfiles({ onClose }) {
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [macro, setMacro] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [newMacro, setNewMacro] = useState("");
  const [newMacroLabel, setNewMacroLabel] = useState("");
  const [newSub, setNewSub] = useState({});

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/admin/clients`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/sectors`, { headers: adminHeaders() }),
      ]);
      setClients(c.data || []);
      setMacro(s.data?.macro || []);
    } catch { toast.error("Caricamento profili fallito"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const subOptions = macro.find((m) => m.slug === form.macro_sector)?.sub || [];

  const saveClient = async () => {
    if (!form.name.trim()) { toast.error("Inserisci il nome cliente"); return; }
    setSaving(true);
    const body = { ...form, default_keywords: (Array.isArray(form.default_keywords) ? form.default_keywords : String(form.default_keywords || "").split(",").map((x) => x.trim()).filter(Boolean)) };
    try {
      if (editId) await axios.put(`${API}/admin/clients/${editId}`, body, { headers: adminHeaders() });
      else await axios.post(`${API}/admin/clients`, body, { headers: adminHeaders() });
      toast.success(editId ? "Profilo aggiornato" : "Profilo creato");
      setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSaving(false); }
  };
  const editClient = (c) => { setEditId(c.id); setForm({ name: c.name, macro_sector: c.macro_sector || "", sub_sector: c.sub_sector || "", tone_of_voice: c.tone_of_voice || "professionale", logo_url: c.logo_url || "", default_keywords: c.default_keywords || [], data_source_label: c.data_source_label || "" }); };
  const delClient = async (c) => {
    if (!window.confirm(`Eliminare il profilo "${c.name}"?`)) return;
    try { await axios.delete(`${API}/admin/clients/${c.id}`, { headers: adminHeaders() }); toast.success("Profilo eliminato"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Eliminazione fallita"); }
  };

  const addMacro = async () => {
    if (!newMacro.trim()) return;
    try { await axios.post(`${API}/admin/sectors/macro`, { name: newMacro.trim(), case_studies_label: newMacroLabel.trim() || "Casi di successo" }, { headers: adminHeaders() }); setNewMacro(""); setNewMacroLabel(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const delMacro = async (slug) => { if (!window.confirm("Eliminare questo macro settore e i suoi sotto-settori?")) return; try { await axios.delete(`${API}/admin/sectors/macro/${slug}`, { headers: adminHeaders() }); load(); } catch { toast.error("Errore"); } };
  const addSub = async (m) => { const name = (newSub[m] || "").trim(); if (!name) return; try { await axios.post(`${API}/admin/sectors/macro/${m}/sub`, { name }, { headers: adminHeaders() }); setNewSub((s) => ({ ...s, [m]: "" })); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } };
  const delSub = async (m, sub) => { try { await axios.delete(`${API}/admin/sectors/macro/${m}/sub/${sub}`, { headers: adminHeaders() }); load(); } catch { toast.error("Errore"); } };

  const ql = q.trim().toLowerCase();
  const filteredMacro = ql
    ? macro.map((m) => ({ ...m, sub: (m.sub || []).filter((s) => s.name.toLowerCase().includes(ql)) })).filter((m) => m.name.toLowerCase().includes(ql) || m.sub.length)
    : macro;
  const macroName = (slug) => macro.find((m) => m.slug === slug)?.name || "—";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="client-profiles-modal">
      <div className="bg-[#121620] border border-[#2D3748] rounded-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3748]">
          <div className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#8B5CF6]" /><h2 className="text-lg font-bold text-white">Profili Cliente</h2></div>
          <button data-testid="close-clients" onClick={onClose} className="text-[#94A3B8] hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-1 px-6 pt-4">
          <button data-testid="tab-clients" onClick={() => setTab("clients")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === "clients" ? "bg-[#8B5CF6] text-white" : "text-[#94A3B8] hover:text-white"}`}>Clienti</button>
          <button data-testid="tab-sectors" onClick={() => setTab("sectors")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tab === "sectors" ? "bg-[#8B5CF6] text-white" : "text-[#94A3B8] hover:text-white"}`}>Settori</button>
        </div>

        {tab === "clients" && (
          <div className="p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-3 bg-[#0B0D12] border border-[#2D3748] rounded-xl p-4">
              <div className="sm:col-span-2 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{editId ? "Modifica profilo" : "Nuovo profilo"}</div>
              <input data-testid="client-name" className={field} placeholder="Nome cliente" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <select data-testid="client-tone" className={field} value={form.tone_of_voice} onChange={(e) => set("tone_of_voice", e.target.value)}>
                {TONES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
              <select data-testid="client-macro" className={field} value={form.macro_sector} onChange={(e) => { set("macro_sector", e.target.value); set("sub_sector", ""); }}>
                <option value="">Macro settore…</option>
                {macro.map((m) => <option key={m.slug} value={m.slug}>{m.name}</option>)}
              </select>
              <select data-testid="client-sub" className={field} value={form.sub_sector} onChange={(e) => set("sub_sector", e.target.value)} disabled={!subOptions.length}>
                <option value="">Sotto-settore…</option>
                {subOptions.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
              <input data-testid="client-logo" className={field} placeholder="URL logo (opzionale)" value={form.logo_url} onChange={(e) => set("logo_url", e.target.value)} />
              <input data-testid="client-keywords" className={field} placeholder="Keyword di default (separate da virgola)" value={Array.isArray(form.default_keywords) ? form.default_keywords.join(", ") : form.default_keywords} onChange={(e) => set("default_keywords", e.target.value)} />
              <div className="sm:col-span-2">
                <input data-testid="client-source-label" className={field} placeholder='Nome fonte dati nei report PDF (es. "Ricerca Enterprise", "Osservatorio Ale")' value={form.data_source_label} onChange={(e) => set("data_source_label", e.target.value)} />
                <p className="text-[10px] text-[#64748B] mt-1">Appare nei PDF come "Fonte dati: …". Se vuoto: "Ricerca Enterprise".</p>
              </div>
              <div className="sm:col-span-2 flex gap-2">
                <button data-testid="save-client" onClick={saveClient} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}{editId ? "Aggiorna" : "Crea profilo"}
                </button>
                {editId && <button onClick={() => { setEditId(null); setForm(empty); }} className="text-sm text-[#94A3B8] hover:text-white px-3">Annulla</button>}
              </div>
            </div>
            <div className="space-y-2" data-testid="clients-list">
              {clients.map((c) => (
                <div key={c.id} className="flex items-center gap-3 bg-[#0B0D12] border border-[#2D3748] rounded-lg px-4 py-3">
                  {c.logo_url ? <img src={c.logo_url} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-[#1e2533] flex items-center justify-center text-[#64748B] text-xs">{c.name?.[0] || "?"}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white flex items-center gap-2">{c.name}{c.is_default && <span className="text-[10px] bg-[#10B981]/15 text-[#34d399] px-1.5 py-0.5 rounded-full">predefinito</span>}</div>
                    <div className="text-[11px] text-[#64748B]">tenant: <span className="font-mono">{c.tenant}</span> · {macroName(c.macro_sector)} · {c.tone_of_voice}</div>
                  </div>
                  <button data-testid={`edit-client-${c.id}`} onClick={() => editClient(c)} className="text-xs text-[#06B6D4] hover:text-[#67e8f9] font-semibold">Modifica</button>
                  {!c.is_default && <button data-testid={`del-client-${c.id}`} onClick={() => delClient(c)} className="text-[#EF4444] hover:text-[#fca5a5]"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "sectors" && (
          <div className="p-6 space-y-4">
            <div className="relative">
              <Search className="w-4 h-4 text-[#64748B] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="sector-search" className={`${field} pl-9`} placeholder="Cerca macro o sotto-settore…" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 bg-[#0B0D12] border border-[#2D3748] rounded-lg p-3">
              <input data-testid="new-macro" className={field} placeholder="Nuovo macro settore" value={newMacro} onChange={(e) => setNewMacro(e.target.value)} />
              <input className={field} placeholder='Etichetta "case studies" (es. Case Cliniche)' value={newMacroLabel} onChange={(e) => setNewMacroLabel(e.target.value)} />
              <button data-testid="add-macro" onClick={addMacro} className="inline-flex items-center gap-1 bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-semibold px-3 py-2 rounded-lg shrink-0"><Plus className="w-4 h-4" />Macro</button>
            </div>
            <div className="space-y-3" data-testid="sectors-list">
              {filteredMacro.map((m) => (
                <div key={m.slug} className="bg-[#0B0D12] border border-[#2D3748] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-white">{m.name} <span className="text-[10px] text-[#64748B] font-normal">· "{m.case_studies_label}"</span></div>
                    <button data-testid={`del-macro-${m.slug}`} onClick={() => delMacro(m.slug)} className="text-[#EF4444] hover:text-[#fca5a5]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {(m.sub || []).map((s) => (
                      <span key={s.slug} className="inline-flex items-center gap-1 bg-[#1e2533] text-[#cbd5e1] text-[11px] px-2 py-1 rounded-full">{s.name}<button onClick={() => delSub(m.slug, s.slug)} className="text-[#64748B] hover:text-[#EF4444]"><X className="w-3 h-3" /></button></span>
                    ))}
                    {!(m.sub || []).length && <span className="text-[11px] text-[#64748B]">Nessun sotto-settore</span>}
                  </div>
                  <div className="flex gap-2">
                    <input className={`${field} py-1.5`} placeholder="Aggiungi sotto-settore" value={newSub[m.slug] || ""} onChange={(e) => setNewSub((s) => ({ ...s, [m.slug]: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") addSub(m.slug); }} />
                    <button data-testid={`add-sub-${m.slug}`} onClick={() => addSub(m.slug)} className="text-xs text-[#06B6D4] hover:text-[#67e8f9] font-semibold px-2 shrink-0">+ Aggiungi</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
